/**
 * WBAgent page-context collection.
 *
 * The agent chat grounds its answers in a snapshot of the user's page (the
 * `wandb-web-page-state` part), a stack of page-content objects (workspace,
 * report, etc.) assembled at send time from values scattered across the page.
 * This module is the non-React core; layer.tsx holds the React wiring, the
 * views/ hold each page's channels and reducer, and collect.ts assembles them.
 *
 * - A channel (`AgentPageContextChannel`) is a typed publish point. A
 *   component publishes a value to it and a layer reducer reads it back,
 *   without either side referencing the other.
 * - A layer (AgentPageContextLayer) is one entry in the stack. A fact
 *   belongs to its nearest enclosing layer, whose reducer reads only the
 *   channels it names. Layers nest, so the stack runs outermost to innermost.
 * - A group (AgentPageContextGroup) buckets repeated instances in a layer (a
 *   report's run sets) under a data key, read back as an array.
 *
 * Collection (collectAgentPageContext) is lazy at send time, so each channel
 * reflects its freshest value. See views/workspace.ts and views/report.ts for
 * worked reducer examples.
 */

import {envIsDev, envIsTest} from '../../../config';

declare const channelType: unique symbol;

/** AgentPageContextChannel is an opaque, typed publish point. */
export interface AgentPageContextChannel<T> {
  // NOTE: Phantom marker so TypeScript tracks T; never set at runtime.
  readonly [channelType]?: T;
}

/** agentPageContextChannel creates a new channel. */
export function agentPageContextChannel<T>(): AgentPageContextChannel<T> {
  return {};
}

/**
 * AgentPageContextReader picks values out of one layer's direct facts. `one`
 * takes the single value of a channel; `groups` takes one reader per instance
 * bucket, for array-valued page content.
 */
export interface AgentPageContextReader {
  one<T>(channel: AgentPageContextChannel<T>): T | undefined;
  groups(): AgentPageContextGroupReader[];
}

/** AgentPageContextGroupReader reads the values in one group bucket. */
export interface AgentPageContextGroupReader {
  one<T>(channel: AgentPageContextChannel<T>): T | undefined;
}

/** AgentPageContextLayerNode identifies one mounted layer instance. */
export interface AgentPageContextLayerNode {
  type: string;
  depth: number;
}

/** AgentPageContextFact is one live value bound to a layer and group. */
export interface AgentPageContextFact {
  channel: AgentPageContextChannel<unknown>;
  layer: AgentPageContextLayerNode | null;
  groupKey: string | null;
  order: number;
  get(): unknown;
}

const facts = new Set<AgentPageContextFact>();

/** registerAgentPageContextFact adds a fact and returns a disposer. */
export function registerAgentPageContextFact(
  fact: AgentPageContextFact
): () => void {
  facts.add(fact);
  return () => {
    facts.delete(fact);
  };
}

/**
 * reduceAgentPageContextLayers runs `reduce` once per mounted layer over that
 * layer's own direct facts, returning the non-null results ordered from the
 * outermost layer to the innermost.
 */
export function reduceAgentPageContextLayers<R>(
  reduce: (type: string, read: AgentPageContextReader) => R | null
): R[] {
  const layers = new Set<AgentPageContextLayerNode>();
  for (const fact of facts) {
    if (fact.layer != null) {
      layers.add(fact.layer);
    }
  }

  const results: Array<{depth: number; value: R}> = [];
  for (const layer of layers) {
    const direct: AgentPageContextFact[] = [];
    for (const fact of facts) {
      if (fact.layer === layer) {
        direct.push(fact);
      }
    }

    const read = makeReader(direct);
    const value = reduce(layer.type, read);
    if (value != null) {
      results.push({depth: layer.depth, value});
    }
  }

  return results.sort((a, b) => a.depth - b.depth).map(result => result.value);
}

// makeReader builds a reader over one layer's direct facts.
function makeReader(direct: AgentPageContextFact[]): AgentPageContextReader {
  const ungrouped = direct.filter(f => f.groupKey == null);

  function valuesOf<T>(
    from: AgentPageContextFact[],
    channel: AgentPageContextChannel<T>
  ): T[] {
    const values: T[] = [];
    for (const fact of from) {
      if (fact.channel === channel) {
        const value = fact.get();
        if (value != null) {
          values.push(value as T);
        }
      }
    }
    return values;
  }

  return {
    one(channel) {
      const values = valuesOf(ungrouped, channel);
      if ((envIsDev || envIsTest) && values.length > 1) {
        // NOTE: one() is single-valued; multiple publishers in one layer
        // signal a mount-point mistake, so flag it instead of silently picking.
        console.warn(
          `usePublishAgentPageContext saw ${values.length} facts for one channel in a single layer; returning the first.`
        );
      }
      return values[0];
    },
    groups() {
      const buckets = new Map<string, AgentPageContextFact[]>();
      for (const fact of direct) {
        if (fact.groupKey != null) {
          const bucket = buckets.get(fact.groupKey);
          if (bucket == null) {
            buckets.set(fact.groupKey, [fact]);
          } else {
            bucket.push(fact);
          }
        }
      }

      return [...buckets.values()]
        .sort((a, b) => minOrder(a) - minOrder(b))
        .map(bucket => ({
          one(channel) {
            return valuesOf(bucket, channel)[0];
          },
        }));
    },
  };
}

function minOrder(bucket: AgentPageContextFact[]): number {
  return bucket.reduce((min, fact) => Math.min(min, fact.order), Infinity);
}
